import { ColDef } from "ag-grid-community";
import { TimeframeTypeEnum } from "../../common/enums/TimeframeTypeEnum";
import { RowDef } from "../../components/ag-grid-extensions/models/RowDef";
import i18n from "../../services/localization-service/i18n";
import { IDateRange } from "../../common/models/IDateRange";
import { AuditActionEnum, IAuditTrail } from "../../services/audit-reporting-data-service/Models";
import { getTimeframeTimeDisplayText } from "../../common/utilities/TimeframeUtilities";
import { getObjectTypeName } from "../../common/utilities/GetObjectTypeName";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import AuditReportingDataService, { IAuditTrailFilter } from "../../services/audit-reporting-data-service/AuditReportingDataService";
import { AuditReportingStateType } from "./AuditReportingState";


const getActionText = (action: AuditActionEnum) : string => {
    switch (action) {
        case AuditActionEnum.Delete:
            return 'Delete';
        case AuditActionEnum.Insert: 
            return 'Insert';
        case AuditActionEnum.None: 
            return '';
        case AuditActionEnum.Update:
            return 'Update';
    }
}

const projectRow = (
    entry: IAuditTrail): RowDef => {

    let row: RowDef = {
    };

    row.id = entry.id;
    row['timestamp'] =  getTimeframeTimeDisplayText(TimeframeTypeEnum.None, entry.timestamp);
    row['objectType'] = entry.objectType !== eLynxObjectTypeEnum.None ? getObjectTypeName(entry.objectType) : '';
    row['objectName'] = entry.objectName;
    row['action'] = getActionText(entry.action);
    row['user'] = entry.userLogin;
    row['userIPAddress'] = entry.userIPAddress;
    row['oldValue'] = entry.oldValue;
    row['newValue'] = entry.newValue;
    row['parentObjectType'] = (entry.parentObjectType ?? eLynxObjectTypeEnum.None) !== eLynxObjectTypeEnum.None ? getObjectTypeName(entry.parentObjectType) : '';
    row['parentObjectName'] = entry.parentName;
    row['additionalInfo'] = entry.additionalInfo;

    return row;
}

const AuditReportingUtils = {

    createGridRows: function (entries: IAuditTrail[] | undefined): RowDef[] {

        const rows: RowDef[] = entries?.map((o) => {

            return projectRow(o);

        }) ?? [];


        return rows;
    },
    createGridColumns: function (): ColDef[] {

        let cols: ColDef[] = [{
            headerName: i18n.t("datetime.timestamp") as string,
            field: 'timestamp',
            sortable: true,
            sort: "desc",
            //flex: 1
            type: "timestamp"
        }, {
            headerName: i18n.t("objectType") as string,
            field: "objectType",
            sortable: true,
            //flex: 1
            type: "text"
        }, {
            headerName: i18n.t("objectName") as string,
            field: "objectName",
            sortable: true,
            //flex: 1
            type: "text"
        }, {
            headerName: i18n.t("auditReporting.action") as string,
            field: "action",
            sortable: true,
            //flex: 1
            type: "text"
        }, {
            headerName: i18n.t("user") as string,
            field: "user",
            sortable: true,
            //flex: 1
            type: "text"
        }, {
            headerName: i18n.t("ipaddress") as string,
            field: "userIPAddress",
            sortable: true,
            //flex: 1
            type: "text"
        }, {
            headerName: i18n.t("auditReporting.oldValue") as string,
            field: "oldValue",
            sortable: true,
            //flex: 1
            type: "text"
        }, {
            headerName: i18n.t("auditReporting.newValue") as string,
            field: "newValue",
            sortable: true,
            //flex: 1
            type: "text"
        }, {
            headerName: i18n.t("auditReporting.parentObjectType") as string,
            field: "parentObjectType",
            sortable: true,
            //flex: 1
            type: "text"
        }, {
            headerName: i18n.t("auditReporting.parentObjectName") as string,
            field: "parentObjectName",
            sortable: true,
            //flex: 1
            type: "text"
        }, {
            headerName: i18n.t("auditReporting.additionalInfo") as string,
            field: "additionalInfo",
            sortable: true,
            //flex: 1
            type: "text"
        }];

        return cols;
    },

    getGridNoDataMessage(tagName: string, dateRange?: IDateRange): string {

        let message = '';

        if (dateRange && dateRange.startDate && dateRange.endDate) {
            message = i18n.t("noData.noDataForObjectWithinDateRange", {
                startDate: dateRange.startDate.toLocaleString(),
                endDate: dateRange.endDate.toLocaleString(),
                objectName: tagName
            });

        } else {
            message = i18n.t("noData.noDataForObject", { objectName: tagName });
        }

        return message;
    },
    async getData(groupID: number, filter: IAuditTrailFilter): Promise<IAuditTrail[]> {

        return AuditReportingDataService.getData(groupID,
            filter.startDate,
            filter.endDate,
            filter.searchText ? undefined : filter.action,
            filter.searchText ? undefined : filter.objectType,
            filter.searchText ? undefined : filter.userLogin,
            filter.searchText ? undefined : filter.objectName,
            filter.searchText ? undefined : filter.oldValue,
            filter.searchText ? undefined : filter.newValue,
            filter.searchText ? undefined : filter.additionalInfo,
            filter.searchText,
            filter.maxRows);
    },
    mapToAuditFilter(state: AuditReportingStateType): IAuditTrailFilter {

        const filter: IAuditTrailFilter = {
            startDate: state.durationOptions.duration.startDate,
            endDate: state.durationOptions.duration.endDate,
            action: state.auditAction,
            objectType: state.objectType,
            objectName: state.objectName,
            userLogin: state.userName,
            oldValue: state.oldValue,
            newValue: state.newValue,
            maxRows: state.maxRows,
            additionalInfo: state.additionalInfo,
            searchText: (state.searchText?.trim()?.length ?? 0) > 0 ? state.searchText?.trim() : undefined
        };

        return filter;
    }
};

export default AuditReportingUtils;
